import EnrollmentForm from "@/components/enrollment/EnrollmentForm";
import RefundForm from "@/components/enrollment/RefundForm";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { formatCurrency } from "@/lib/utils/currency";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import {
    EnrollmentTier,
    getEnrollmentTierLabel,
    RequestHelpType,
} from "@/lib/utils/enrollment";
import { Status } from "@/types/enums";
import { FieldWithEnrollment, ImageryExtent } from "@/types/field";
import {
    faDownload,
    faArrowUpFromBracket,
    faArrowUpShortWide,
    faArrowDownShortWide,
    faPencil,
    faArrowRotateLeft,
    faPlus,
    faMoneyCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    Chip,
    Divider,
    Spinner,
    Switch,
    Table,
    TableColumn,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    useDisclosure,
} from "@heroui/react";
import { createFileRoute } from "@tanstack/react-router";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { useMemo, useState, useCallback, useRef } from "react";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";
import { Link } from "@tanstack/react-router";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import { useI18n } from "@/lib/i18n/useI18n";

const SENTRY_PROVIDER_ID = 77; // Demo Fields

export const Route = createFileRoute("/dashboard/enrollment/fields")({
    component: RouteComponent,
});

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { formatDateMMDDYYYY } = useI18n();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const enrollmentQuery = useGetRequest<FieldWithEnrollment[]>({
        url: "fields/enrollment_status/",
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const remainingBalance = useGetRequest<{
        remaining_retail_value: number;
    }>({
        url: "field_enrollments/remaining_retail_value/",
    });
    const { userIsSentinel, isConsultant } = useAuth();

    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
    const [showEnrollmentForm, setShowEnrollmentForm] =
        useState<boolean>(false);
    const [showRefundForm, setShowRefundForm] = useState<boolean>(false);

    const patchField = useMutateRequest({
        method: "patch",

        errorMessage: "Failed to update field",
        successMessage: "Successfully updated field",
    });

    const columns: ColumnDef<FieldWithEnrollment>[] = useMemo(() => {
        const tableColumns: ColumnDef<FieldWithEnrollment>[] = [
            {
                id: "select",
                header: "",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                size: 50,
                cell: ({ row }) => {
                    return (
                        <Checkbox
                            size="sm"
                            isDisabled={!!row.original.active_enrollment}
                            isReadOnly={!!row.original.active_enrollment}
                            isSelected={selectedRowIds.includes(
                                row.original.id,
                            )}
                            onValueChange={(value) => {
                                if (value) {
                                    setSelectedRowIds([
                                        ...selectedRowIds,
                                        row.original.id,
                                    ]);
                                } else {
                                    setSelectedRowIds(
                                        selectedRowIds.filter(
                                            (r) => r !== row.original.id,
                                        ),
                                    );
                                }
                            }}
                        />
                    );
                },
            },
            {
                header: "Active",
                id: "status",
                accessorKey: "status",
                cell: ({ row }) => {
                    return (
                        <Switch
                            size="sm"
                            isSelected={row.original.status == 1}
                            onValueChange={(isSelected) => {
                                patchField.mutate(
                                    {
                                        url: `fields/${row.original.id}/`,
                                        data: {
                                            status: isSelected ? 1 : 2,
                                        },
                                    },
                                    {
                                        onSuccess: () => {
                                            enrollmentQuery.refetch();
                                        },
                                    },
                                );
                            }}
                        />
                    );
                },
                size: 80,
            },
            {
                id: "farm",
                header: "Farm/CSP",
                accessorFn: (row) => row.grower.name,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col">
                            <div>{row.original.grower?.name}</div>
                            <div className="text-xs text-gray-500">
                                {row.original.grower?.service_provider?.name ??
                                    "N/A"}
                            </div>
                        </div>
                    );
                },
                size: 250,
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row) => row.name,
                cell: ({ row }) => {
                    return (
                        <Link
                            to="/dashboard/fields/$fieldId/setup"
                            params={{ fieldId: row.original.id.toString() }}
                            target="_blank"
                        >
                            <div className="flex flex-col items-start justify-between w-full">
                                {row.original.name}
                                <div className="flex flex-row gap-1 justify-start">
                                    <Chip
                                        color={
                                            row.original.active_enrollment
                                                ? "primary"
                                                : "default"
                                        }
                                        radius="sm"
                                        classNames={{
                                            base: "text-[0.6rem]",
                                            content: "p-[0.15rem]",
                                        }}
                                    >
                                        {getEnrollmentTierLabel(
                                            row.original.active_enrollment
                                                ?.enrollment_tier,
                                        )}
                                    </Chip>
                                    {!!row.original.active_enrollment && (
                                        <Chip
                                            color={
                                                row.original.active_enrollment
                                                    .insights_active
                                                    ? "secondary"
                                                    : "default"
                                            }
                                            radius="sm"
                                            classNames={{
                                                base: "text-[0.6rem]",
                                                content: "p-[0.15rem]",
                                            }}
                                        >
                                            {row.original.active_enrollment
                                                .insights_active
                                                ? "Insights Active"
                                                : "Insights Inactive"}
                                        </Chip>
                                    )}
                                </div>
                            </div>
                        </Link>
                    );
                },
                size: 250,
            },
            {
                id: "enrollment",
                header: "Enrollment",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.start_date;
                },
                cell: ({ row }) => {
                    const e = row.original.active_enrollment;
                    if (!e) return null;
                    const today = getTodayWithTimeTravel();
                    const end = dayjs(e.end_date);
                    const daysUntilEnd = end.diff(today, "day");
                    const enrollmentExpiresSoon =
                        daysUntilEnd >= 0 &&
                        daysUntilEnd <= 30;
                    return (
                        <div
                            className={classNames(
                                "flex flex-col rounded-md px-1.5 py-1 mx-1 -my-0.5",
                                enrollmentExpiresSoon &&
                                "bg-warning-200",
                            )}
                            title={
                                enrollmentExpiresSoon
                                    ? "Enrollment ends within 30 days"
                                    : undefined
                            }
                        >
                            <div>{formatDateMMDDYYYY(e.start_date)}</div>
                            <div className="text-xs text-gray-500">
                                {formatDateMMDDYYYY(e.end_date)}
                            </div>
                        </div>
                    );
                },
                size: 110,
            },
            {
                id: "insights",
                header: "Insights",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.insights_start_date;
                },
                cell: ({ row }) => {
                    const e = row.original.active_enrollment;
                    if (!e) return null;
                    return (
                        <div className="flex flex-col">
                            <div>{formatDateMMDDYYYY(e.insights_start_date)}</div>
                            <div className="text-xs text-gray-500">
                                {formatDateMMDDYYYY(e.insights_end_date)}
                            </div>
                        </div>
                    );
                },
                size: 120,
            },
            {
                id: "acres",
                header: "Acres",
                accessorFn: (row: FieldWithEnrollment) => {
                    return (
                        row.active_enrollment?.acres ??
                        row.imagery_boundary_acres
                    );
                },
                size: 100,
            },
            {
                id: "boundaryType",
                header: "Boundary Type",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.imagery_extent;
                },
                cell: ({ row }) => {
                    return (
                        <ButtonGroup
                            size="sm"
                            isDisabled={!!row.original.active_enrollment}
                        >
                            <Button
                                size="sm"
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            url: `fields/${row.original.id}/`,
                                            data: {
                                                imagery_extent:
                                                    ImageryExtent.FIELD,
                                            },
                                        },
                                        {
                                            onSuccess: () => {
                                                enrollmentQuery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                Field
                            </Button>
                            <Button
                                size="sm"
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "default"
                                        : "primary"
                                }
                                isDisabled={!row.original.has_irrigation_system}
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            url: `fields/${row.original.id}/`,
                                            data: {
                                                imagery_extent:
                                                    ImageryExtent.IRRIGATED_REGION,
                                            },
                                        },
                                        {
                                            onSuccess: () => {
                                                enrollmentQuery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                Irrigated
                            </Button>
                        </ButtonGroup>
                    );
                },
            },
            {
                id: "price",
                header: "Price",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.retail_price_per_acre;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment
                                    ?.retail_price_per_acre,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "total",
                header: "Total",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.credits;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment?.credits,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "actions",
                header: "Actions",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row gap-1 w-full">
                            {!row.original.active_enrollment && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setSelectedRowIds([row.original.id]);
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPlus} />
                                    }
                                >
                                    Enroll
                                </Button>
                            )}
                            {row.original.active_enrollment && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setSelectedRowIds([row.original.id]);
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPencil} />
                                    }
                                >
                                    Edit
                                </Button>
                            )}
                            {row.original.active_enrollment &&
                                !row.original.active_enrollment.refunded_on &&
                                userIsSentinel && (
                                    <Button
                                        color="primary"
                                        size="sm"
                                        fullWidth
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowRotateLeft}
                                            />
                                        }
                                        onPress={() => {
                                            setSelectedRowIds([
                                                row.original.id,
                                            ]);
                                            setShowRefundForm(true);
                                        }}
                                    >
                                        Refund
                                    </Button>
                                )}
                        </div>
                    );
                },
            },
        ];

        if (!userIsSentinel) {
            tableColumns.splice(2, 1);
        }

        return tableColumns;
    }, [enrollmentQuery, patchField, selectedRowIds, userIsSentinel, formatDateMMDDYYYY]);

    const fieldsToEnroll = useMemo(() => {
        return (
            enrollmentQuery?.data?.filter((row) =>
                selectedRowIds.includes(row.id),
            ) ?? []
        );
    }, [enrollmentQuery, selectedRowIds]);

    const [searchValue, setSearchValue] = useState("");
    const [serviceProviderFilter, setServiceProviderFilter] =
        useState<string>("");
    const [farmFilter, setFarmFilter] = useState<string>("");
    const serviceProviderOptions = useMemo(() => {
        return enrollmentQuery?.data
            ?.map((field) => field.grower.service_provider)
            .filter(
                (sp, index, self) =>
                    sp && index === self.findIndex((s) => s?.id === sp.id),
            ) as { id: number; name: string }[];
    }, [enrollmentQuery?.data]);
    const farmOptions = useMemo(() => {
        return enrollmentQuery?.data
            ?.map((field) => field.grower)
            .filter(
                (grower, index, self) =>
                    index === self.findIndex((g) => g.id === grower.id),
            ) as { id: number; name: string }[];
    }, [enrollmentQuery?.data]);

    const filteredRows = useMemo(() => {
        let rows = enrollmentQuery?.data ?? [];
        if (searchValue) {
            rows = rows.filter(
                (row) =>
                    row.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    row.grower.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    row.grower.service_provider?.name
                        ?.toLowerCase()
                        .includes(searchValue.toLowerCase()),
            );
        }
        if (serviceProviderFilter) {
            rows = rows.filter(
                (row) =>
                    row.grower.service_provider?.id.toString() ===
                    serviceProviderFilter,
            );
        }
        if (farmFilter) {
            rows = rows.filter(
                (row) => row.grower.id.toString() === farmFilter,
            );
        }
        return rows;
    }, [enrollmentQuery?.data, searchValue, serviceProviderFilter, farmFilter]);

    const table = useReactTable({
        columns,
        data: filteredRows,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            sorting: [
                {
                    id: "enrollment",
                    desc: false,
                },
            ],
        },
    });

    const { rows } = table.getRowModel();

    const downloadPDF = useCallback(() => {
        const filteredData = filteredRows;
        const columns = [
            "Status",
            "Farm",
            "Field",
            "Enrollment Status",
            "Enrollment",
            "Insights",
            "Acres",
            "Boundary Type",
            "Tier",
            "Price",
            "Total",
        ];
        const isSentinel = userIsSentinel;
        if (isSentinel) {
            columns.splice(1, 0, "CSP");
        }
        const rows = filteredData.map((field) => {
            const e = field.active_enrollment;
            const enrollmentDates = e
                ? `${formatDateMMDDYYYY(e.start_date)}\n${formatDateMMDDYYYY(e.end_date)}`
                : "";
            const insightsDates = e
                ? `${formatDateMMDDYYYY(e.insights_start_date)}\n${formatDateMMDDYYYY(e.insights_end_date)}`
                : "";
            const row = [
                field.status === Status.Active ? "Active" : "Inactive",
                field.grower.name,
                field.name,
                field.active_enrollment
                    ? field.active_enrollment.refunded_on
                        ? "Enrolled (Refunded)"
                        : field.active_enrollment.is_scheduled
                            ? "Scheduled"
                            : "Enrolled"
                    : "Inactive",
                enrollmentDates,
                insightsDates,
                field.active_enrollment?.acres ?? field.imagery_boundary_acres,
                field.imagery_extent == ImageryExtent.FIELD
                    ? "Field"
                    : "Irrigated",
                getEnrollmentTierLabel(
                    field.active_enrollment?.enrollment_tier,
                ),
                field.active_enrollment?.retail_price_per_acre,
                field.active_enrollment?.credits,
            ];
            if (isSentinel) {
                row.splice(1, 0, field.grower.service_provider?.name);
            }
            return row as (string | number)[];
        });

        const doc = new jsPDF();
        const image = new Image();
        image.src = sentinelLogo;
        image.onload = () => {
            doc.addImage(image, "PNG", 15, 10, 80, 20);
            const today = dayjs().format("MM/DD/YYYY");
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.setFontSize(12);
            doc.text(`${today}`, pageWidth - 40, 20);
            const textWidth = doc.getTextWidth("Enrollment Summary");
            const x = (pageWidth - textWidth) / 2;
            doc.setFontSize(16);
            doc.text("Enrollment Summary", x, 35);
            doc.setFontSize(6);
            autoTable(doc, {
                columns: columns,
                body: rows,
                startY: 40,
                theme: "grid",
                bodyStyles: {
                    fontSize: 8,
                    overflow: "linebreak",
                },
                headStyles: {
                    fontSize: 8,
                    fillColor: [0, 51, 102],
                    textColor: [255, 255, 255],
                    overflow: "linebreak",
                },
            });
            doc.save("Sentinel Report.pdf");
        };
    }, [filteredRows, userIsSentinel, formatDateMMDDYYYY]);

    const ref = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33,
        getScrollElement: () => ref.current,
        measureElement:
            typeof window !== "undefined" &&
                navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    const summary = useMemo(() => {
        return (
            filteredRows?.reduce(
                (acc, fe) => {
                    if (fe.grower.service_provider?.id === SENTRY_PROVIDER_ID) {
                        return acc;
                    }
                    if (fe.active_enrollment) {
                        if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Standard
                        ) {
                            acc.standardAcres += fe.active_enrollment.acres;
                            acc.standardValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.AdvancedV2
                        ) {
                            acc.advancedAcres += fe.active_enrollment.acres;
                            acc.advancedValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Complete
                        ) {
                            acc.completeAcres += fe.active_enrollment.acres;
                            acc.completeValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Irrigation
                        ) {
                            acc.irrigationAcres += fe.active_enrollment.acres;
                            acc.irrigationValue += fe.active_enrollment.credits;
                        }
                        acc.enrolledAcres += fe.active_enrollment.acres;
                        acc.enrolledValue += fe.active_enrollment.credits;
                    } else {
                        if (fe.status == Status.Active) {
                            acc.activeUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        } else {
                            acc.inactiveUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        }
                    }
                    return acc;
                },
                {
                    standardAcres: 0,
                    advancedAcres: 0,
                    completeAcres: 0,
                    irrigationAcres: 0,
                    enrolledAcres: 0,
                    standardValue: 0,
                    advancedValue: 0,
                    completeValue: 0,
                    irrigationValue: 0,
                    enrolledValue: 0,
                    activeUnenrolledAcres: 0,
                    inactiveUnenrolledAcres: 0,
                },
            ) ?? {}
        );
    }, [filteredRows]);

    const requestHelp = useMutateRequest({
        url: `field_enrollments/request_help/`,
        method: "post",
        errorMessage: "Failed to request help",
        successMessage: "Your request has been sent to your account manager",
    });

    const requestAdditionalBalance = useCallback(
        () =>
            requestHelp.mutate({
                data: {
                    type: RequestHelpType.request_balance_update,
                },
            }),
        [requestHelp],
    );

    return (
        <div className="p-2 flex flex-col w-full h-full">
            <div className="flex flex-row justify-between items-center">
                <FieldFilterBar
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    serviceProviderId={serviceProviderFilter}
                    onServiceProviderChange={(value) =>
                        setServiceProviderFilter(value ?? "")
                    }
                    serviceProviderOptions={serviceProviderOptions?.map(
                        (sp) => ({
                            key: sp.id.toString(),
                            label: sp.name,
                        }),
                    )}
                    growerId={farmFilter}
                    onGrowerChange={(value) => setFarmFilter(value ?? "")}
                    growerOptions={farmOptions?.map((g) => ({
                        key: g.id.toString(),
                        label: g.name,
                    }))}
                />
                <div className="flex flex-row gap-2 items-center">
                    {isConsultant && (
                        <div className="text-sm flex items-center gap-2 min-w-[500px]">
                            <span>Remaining Retail Balance:</span>
                            <Chip color="primary" radius="sm">
                                {formatCurrency(
                                    remainingBalance?.data
                                        ?.remaining_retail_value,
                                )}
                            </Chip>
                            <Button
                                size="sm"
                                color="primary"
                                onPress={requestAdditionalBalance}
                                isLoading={requestHelp.isPending}
                            >
                                Request Additional Balance
                            </Button>
                        </div>
                    )}
                    <Button
                        color="primary"
                        onPress={onOpen}
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faMoneyCheck} />}
                    >
                        Totals
                    </Button>
                    <Button
                        color="primary"
                        onPress={downloadPDF}
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                    >
                        Download PDF
                    </Button>
                    <Button
                        color="secondary"
                        onPress={() => {
                            setShowEnrollmentForm(true);
                        }}
                        size="sm"
                        isDisabled={selectedRowIds.length === 0}
                        startContent={
                            <FontAwesomeIcon icon={faArrowUpFromBracket} />
                        }
                    >
                        Enroll Selected Fields
                    </Button>
                </div>
            </div>
            <div
                ref={ref}
                className="overflow-y-scroll text-sm bg-white w-full h-screen"
            >
                <table style={{ width: "100%" }}>
                    <thead
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-white w-full"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr className="border-b py-[0.25rem] border-gray-700 w-full flex flex-row items-center justify-items-center">
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.column.getSize(),
                                            }}
                                            className="pointer-events-none"
                                        >
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                className={classNames({
                                                    "hover:bg-gray-200 rounded-lg p-1 text-sm pointer-events-auto w-full flex text-start":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                    "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownShortWide
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: "relative",
                            width: table.getCenterTotalSize(),
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[
                                virtualRow.index
                            ] as Row<FieldWithEnrollment>;
                            return (
                                <tr
                                    data-index={virtualRow.index}
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    }
                                    key={row.id}
                                    style={{
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: "100%",
                                    }}
                                    className="border-b py-[0.4rem] border-gray-700 flex flex-row items-center justify-items-center"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {enrollmentQuery.isLoading && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <Spinner size="lg" color="primary" label="Loading..." />
                    </div>
                )}
            </div>
            {showEnrollmentForm && (
                <EnrollmentForm
                    fieldsToEnroll={fieldsToEnroll}
                    remainingBalance={
                        remainingBalance?.data?.remaining_retail_value ?? 0
                    }
                    existingEnrollment={
                        fieldsToEnroll[0].active_enrollment
                            ? fieldsToEnroll[0]
                            : undefined
                    }
                    onClose={(shouldRefetch) => {
                        if (shouldRefetch) {
                            enrollmentQuery.refetch();
                        }
                        setShowEnrollmentForm(false);
                    }}
                />
            )}
            {showRefundForm && (
                <RefundForm
                    enrollment={fieldsToEnroll[0]}
                    onClose={() => {
                        setShowRefundForm(false);
                        enrollmentQuery.refetch();
                    }}
                />
            )}
            {isOpen && (
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalContent>
                        <ModalHeader>Totals</ModalHeader>
                        <ModalBody>
                            <Card>
                                <CardHeader className="text-lg font-semibold pb-0">
                                    Enrolled Fields
                                </CardHeader>
                                <Divider />
                                <CardBody>
                                    <Table removeWrapper>
                                        <TableHeader>
                                            <TableColumn key="tier" align="start" hideHeader={true}>Tier</TableColumn>
                                            <TableColumn key="acres" align="end">Acres</TableColumn>
                                            <TableColumn key="value" align="end">Value</TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Standard</TableCell>
                                                <TableCell>{summary.standardAcres.toLocaleString()}</TableCell>
                                                <TableCell>{formatCurrency(summary.standardValue)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Irrigation</TableCell>
                                                <TableCell>{summary.irrigationAcres.toLocaleString()}</TableCell>
                                                <TableCell>{formatCurrency(summary.irrigationValue)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Advanced</TableCell>
                                                <TableCell>{summary.advancedAcres.toLocaleString()}</TableCell>
                                                <TableCell>{formatCurrency(summary.advancedValue)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Complete</TableCell>
                                                <TableCell>{summary.completeAcres.toLocaleString()}</TableCell>
                                                <TableCell>{formatCurrency(summary.completeValue)}</TableCell>
                                            </TableRow>
                                            <TableRow className="bg-gray-100">
                                                <TableCell className="font-bold">Totals</TableCell>
                                                <TableCell className="font-bold">{summary.enrolledAcres.toLocaleString()}</TableCell>
                                                <TableCell className="font-bold">{formatCurrency(summary.enrolledValue)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader className="text-lg font-semibold pb-0">
                                    Unenrolled Fields
                                </CardHeader>
                                <Divider />
                                <CardBody>
                                    <Table removeWrapper>
                                        <TableHeader>
                                            <TableColumn key="active" align="start">Active Field Acres</TableColumn>
                                            <TableColumn key="inactive" align="start">Inactive Field Acres</TableColumn>
                                            <TableColumn key="total" align="end">Totals</TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>{summary.activeUnenrolledAcres.toLocaleString()}</TableCell>
                                                <TableCell>{summary.inactiveUnenrolledAcres.toLocaleString()}</TableCell>
                                                <TableCell>{(summary.activeUnenrolledAcres + summary.inactiveUnenrolledAcres).toLocaleString()}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardBody>
                            </Card>

                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}
